import API_BASE from '../config';
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, BarChart3, Globe, Users, Lock, Presentation,
  ChevronRight, Check, Database, ArrowRight,
  ShieldCheck, Search, AlertTriangle, CheckCircle2,
  XCircle, Loader2, Eye, EyeOff,
} from "lucide-react";
import { useNavigate } from "react-router-dom";


const features = [
  { icon: Database, title: "See Your Exact Financial Exposure", desc: "Converts technical vulnerabilities into precise financial exposure figures calibrated against IBM & Verizon DBIR 2024 sector data." },
  { icon: BarChart3, title: "Never Fail A Compliance Audit", desc: "10,000 simulations per audit produce P90 and P99 confidence intervals, the same methodology used by Marsh and Aon." },
  { icon: Globe, title: "Know If You Are Already Compromised", desc: "Automated mapping across 356+ regulatory frameworks across 34 countries & jurisdictions. GDPR, DORA, FCA, NDPA, CBN, PCI-DSS, SEC, NIS2 and more." },
  { icon: Shield, title: "Walk Into Your Board Meeting Ready", desc: "Domain monitoring infrastructure watches your corporate email domain for credential exposures. Alerts delivered to your dashboard when new breaches are detected." },
  { icon: Users, title: "Reduce Your Cyber Insurance Premium", desc: "Third-party ecosystem risk quantified per vendor. SOC2, ISO 27001, GDPR posture cross-referenced with your audit profile." },
  { icon: Lock, title: "Achieve The Standards Enterprise Demands", desc: "ISO 27001, NIST CSF, GDPR, NDPA, CBN, HIPAA, FCA, MAS TRM and 65+ more frameworks built in across 34 countries with gap analysis and prioritised remediation roadmap." },
];

const plans = [
  { tier: "Starter", monthlyPrice: 199, annualPrice: 159, features: ["1 Entity Assessment","Security Posture Score (0-100)","8-Page Starter Audit Report","Financial Exposure Calculation","Regulatory Compliance Radar","Domain Threat Monitoring"], highlight: false },
  { tier: "Professional", monthlyPrice: 499, annualPrice: 399, features: ["2 Entity Assessments","Full 13-Page Executive Report","Monte Carlo P90/P99 Intervals","Compliance Intelligence","Insurance Intelligence","Vendor Risk Matrix + 2 Team Members"], highlight: true },
  { tier: "Enterprise", monthlyPrice: 999, annualPrice: 799, features: ["5 Entity Assessments","19-Slide Board Intelligence Deck","VicShield Compliance Intelligence","VicShield Insurance Intelligence","Sector Benchmarking","Full API Access + 10 Team Members"], highlight: false },
];


type OtpStep = "domain" | "email" | "otp" | "scanning" | "done" | "error" | "rate_limited";

interface ScanResult {
  domain: string; status: string; critical_count?: number;
  risk_findings?: { name: string; risk: string; reason: string; port: string }[];
  cve_findings?: { cve: string; severity: string; cvss: number; summary: string }[];
  open_ports_count?: number; total_leaks?: number; org?: string; ip?: string;
  is_masked?: boolean; upgrade_message?: string; shodan_active?: boolean;
  hibp_active?: boolean; message?: string;
}

const RISK_COLOUR: Record<string, string> = {
  CRITICAL: "text-red-500", HIGH: "text-orange-500", MEDIUM: "text-amber-500",
  LOW: "text-blue-400", INFO: "text-slate-400",
};

const STATUS_META: Record<string, { label: string; color: string; icon: React.FC<any> }> = {
  CRITICAL_EXPOSURE: { label: "Critical Exposure", color: "text-red-500", icon: XCircle },
  HIGH_EXPOSURE:     { label: "High Exposure",     color: "text-orange-500", icon: AlertTriangle },
  PROTECTED:         { label: "CDN Protected",     color: "text-blue-400",   icon: CheckCircle2 },
  MONITORED:         { label: "Monitored",         color: "text-amber-500",  icon: AlertTriangle },
  CLEAN:             { label: "Clean",             color: "text-emerald-500",icon: CheckCircle2 },
  VULNERABLE:        { label: "Breach Detected",   color: "text-red-500",    icon: XCircle },
  CLEAR:             { label: "No Breaches Found", color: "text-emerald-500",icon: CheckCircle2 },
  PENDING:           { label: "Monitoring Active", color: "text-slate-400",  icon: Eye },
};

function ScanResultCard({ result, domain, onSignUp }: { result: ScanResult; domain: string; onSignUp: () => void }) {
  const meta = STATUS_META[result.status] || STATUS_META["MONITORED"];
  const Icon = meta.icon;
  const hasCritical = (result.critical_count ?? 0) > 0;
  const hasFindings = (result.risk_findings?.length ?? 0) > 0;
  const hasBreaches = (result.total_leaks ?? 0) > 0;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="mt-4 bg-[#0f172a] rounded-3xl border border-white/10 overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Domain Intelligence</p>
            <p className="text-white font-black text-lg tracking-tight">{domain}</p>
          </div>
          <div className={`flex items-center gap-2 ${meta.color}`}>
            <Icon size={16} />
            <span className="text-xs font-black uppercase tracking-widest">{meta.label}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x divide-white/10 border-b border-white/10">
        {[
          { label: "Open Ports", value: result.open_ports_count !== undefined ? String(result.open_ports_count) : "—", danger: (result.open_ports_count ?? 0) > 0 },
          { label: "Critical Risks", value: result.critical_count !== undefined ? String(result.critical_count) : "—", danger: hasCritical },
          { label: "Breach Records", value: result.total_leaks !== undefined ? String(result.total_leaks) : "—", danger: hasBreaches },
        ].map((s, i) => (
          <div key={i} className="px-5 py-4 text-center">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1">{s.label}</p>
            <p className={`text-2xl font-black tracking-tighter ${s.danger ? "text-red-500" : "text-white"}`}>{s.value}</p>
          </div>
        ))}
      </div>
      {hasFindings && (
        <div className="px-6 py-4 space-y-2 border-b border-white/10">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-3">Exposure Summary</p>
          {result.risk_findings!.slice(0, 3).map((f, i) => (
            <div key={i} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-slate-600 font-black text-[10px] font-mono bg-white/5 px-2 py-0.5 rounded">
                  {f.port ? "••••" : "••••"}
                </span>
                <span className="text-slate-300 text-xs font-bold truncate">{f.name ? "••••" : "••••"}</span>
              </div>
              <span className={`text-[10px] font-black uppercase tracking-widest flex-shrink-0 ${RISK_COLOUR[f.risk] ?? "text-slate-400"}`}>{f.risk}</span>
            </div>
          ))}
          {(result.risk_findings!.length > 3) && (
            <p className="text-[10px] text-slate-600 font-bold pt-1">+{result.risk_findings!.length - 3} more findings — sign up to view</p>
          )}
        </div>
      )}
      <div className="px-6 py-5">
        <div className="bg-red-600/10 border border-red-600/20 rounded-2xl px-5 py-4 mb-4">
          <div className="flex items-start gap-3">
            <EyeOff size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-400 font-medium leading-relaxed">
              <span className="text-white font-bold">Partial scan.</span>{" "}
              {result.upgrade_message ?? "Sign up for a free 7-day trial to unlock the full domain intelligence report including exact ports, CVE details, and breach remediation steps."}
            </p>
          </div>
        </div>
        <button onClick={onSignUp}
          className="w-full bg-red-600 text-white py-3.5 rounded-xl font-black text-[11px]
            uppercase tracking-widest hover:bg-white hover:text-red-600
            transition-all active:scale-[0.98] flex items-center justify-center gap-2">
          Claim Full Report — Free 7-Day Trial
          <ArrowRight size={14} />
        </button>
        <p className="text-center text-[10px] text-slate-600 font-bold uppercase tracking-widest mt-2">No credit card required</p>
      </div>
    </motion.div>
  );
}

function DomainScanner({ onSignUp }: { onSignUp: () => void }) {
  const [step,      setStep]      = useState<OtpStep>("domain");
  const [domain,    setDomain]    = useState("");
  const [email,     setEmail]     = useState("");
  const [otp,       setOtp]       = useState("");
  const [result,    setResult]    = useState<ScanResult | null>(null);
  const [errorMsg,  setErrorMsg]  = useState("");
  const [sending,   setSending]   = useState(false);
  const [verifying, setVerifying] = useState(false);
  const domainRef = useRef<HTMLInputElement>(null);
  const emailRef  = useRef<HTMLInputElement>(null);
  const otpRef    = useRef<HTMLInputElement>(null);

  const cleanDomain = (raw: string) =>
    raw.trim().toLowerCase()
      .replace("https://", "").replace("http://", "")
      .replace("www.", "").split("/")[0].split("?")[0].replace(/\.$/, "");

  const handleDomainSubmit = () => {
    const d = cleanDomain(domain);
    if (!d || !d.includes(".")) { domainRef.current?.focus(); return; }
    setDomain(d);
    setStep("email");
    setTimeout(() => emailRef.current?.focus(), 100);
  };

  const handleSendOtp = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) { emailRef.current?.focus(); return; }
    setSending(true); setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/public/verify-domain/send`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: e, domain }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.detail ?? "Failed to send code.";
        if (msg === "Not Found" || res.status === 404) {
          setErrorMsg("Unable to verify this domain. Please ensure the email domain matches your company website.");
        } else {
          setErrorMsg(msg);
        }
        return;
      }
      setStep("otp");
      setTimeout(() => otpRef.current?.focus(), 100);
    } catch { setErrorMsg("Unable to connect. Please try again."); }
    finally { setSending(false); }
  };

  const handleVerifyAndScan = async () => {
    const code = otp.trim();
    if (code.length !== 6) { otpRef.current?.focus(); return; }
    setVerifying(true); setErrorMsg("");
    try {
      const vRes = await fetch(`${API_BASE}/api/public/verify-domain/confirm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code }),
      });
      const vData = await vRes.json();
      if (!vRes.ok) { setErrorMsg(vData.detail ?? "Incorrect code."); setVerifying(false); return; }
      setStep("scanning");
      const sRes = await fetch(`${API_BASE}/api/public/scan`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });
      if (sRes.status === 429) {
        const d = await sRes.json(); setStep("rate_limited");
        setErrorMsg(d.detail ?? "Scan limit reached."); return;
      }
      if (!sRes.ok) {
        const d = await sRes.json(); setStep("error");
        setErrorMsg(d.detail ?? "Scan failed."); return;
      }
      const sData = await sRes.json();
      setResult(sData.results); setStep("done");
    } catch { setStep("error"); setErrorMsg("Unable to connect. Please try again."); }
    finally { setVerifying(false); }
  };

  const reset = () => { setStep("domain"); setDomain(""); setEmail(""); setOtp(""); setResult(null); setErrorMsg(""); };

  return (
    <div className="w-full max-w-xl mx-auto">

      {step === "domain" && (
        <>
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-500 mb-3 text-center">
            Free Domain Scan — Verified Corporate Access Only
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input ref={domainRef} type="text" value={domain}
                onChange={e => setDomain(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleDomainSubmit()}
                placeholder="yourcompany.com" spellCheck={false} autoComplete="off"
                className="w-full pl-10 pr-4 py-3.5 bg-white border-2 border-slate-200 rounded-xl
                  text-slate-900 text-sm font-medium placeholder:text-slate-300
                  focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all" />
            </div>
            <button onClick={handleDomainSubmit}
              className="bg-red-600 text-white px-6 py-3.5 rounded-xl font-black text-[11px]
                uppercase tracking-widest hover:bg-slate-900 transition-all active:scale-95
                shadow-lg shadow-red-900/30 whitespace-nowrap">
              Scan Now
            </button>
          </div>
          <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl px-5 py-3 max-w-lg mx-auto">
            <div className="flex items-start gap-2">
              <ShieldCheck size={13} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] font-medium text-slate-500 leading-relaxed text-left">
                <strong className="text-slate-700">🔒 Safe & Passive</strong> — VicShield only
                checks publicly available infrastructure data. We never probe, test, or access
                your systems. No installation required.
              </p>
            </div>
          </div>
        </>
      )}

      {step === "email" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border-2 border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={16} className="text-red-600" />
            <p className="text-xs font-black text-slate-900 uppercase tracking-wide">Verify Domain Ownership</p>
          </div>
          <p className="text-[11px] text-slate-500 font-medium mb-4 leading-relaxed">
            To protect <strong className="text-slate-900">{domain}</strong>, enter your
            corporate email. We will send a 6-digit code to confirm you work there.
          </p>
          <input ref={emailRef} type="email" value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSendOtp()}
            placeholder={`you@${domain}`}
            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900
              text-sm font-medium placeholder:text-slate-300 focus:outline-none
              focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all mb-3" />
          {errorMsg && <p className="text-[11px] text-red-600 font-bold mb-3">{errorMsg}</p>}
          <div className="flex gap-2">
            <button onClick={reset}
              className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-[11px]
                font-black uppercase tracking-widest text-slate-400 hover:border-slate-300 transition-all">
              ← Back
            </button>
            <button onClick={handleSendOtp} disabled={sending}
              className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-black text-[11px]
                uppercase tracking-widest hover:bg-slate-900 transition-all disabled:opacity-60
                flex items-center justify-center gap-2">
              {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send Verification Code"}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 font-medium mt-3 text-center">
            Must be a corporate email at @{domain} — personal emails not accepted
          </p>
        </motion.div>
      )}

      {step === "otp" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border-2 border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <p className="text-xs font-black text-slate-900 uppercase tracking-wide">Enter Verification Code</p>
          </div>
          <p className="text-[11px] text-slate-500 font-medium mb-4 leading-relaxed">
            We sent a 6-digit code to <strong className="text-slate-900">{email}</strong>.
            Enter it below. Code expires in 10 minutes.
          </p>
          <input ref={otpRef} type="text" value={otp}
            onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            onKeyDown={e => e.key === "Enter" && handleVerifyAndScan()}
            placeholder="000000" maxLength={6}
            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900
              text-xl font-black tracking-[0.5em] text-center placeholder:text-slate-200
              placeholder:tracking-widest focus:outline-none focus:border-red-400
              focus:ring-2 focus:ring-red-100 transition-all mb-3" />
          {errorMsg && <p className="text-[11px] text-red-600 font-bold mb-3 text-center">{errorMsg}</p>}
          <div className="flex gap-2">
            <button onClick={() => { setStep("email"); setOtp(""); setErrorMsg(""); }}
              className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-[11px]
                font-black uppercase tracking-widest text-slate-400 hover:border-slate-300 transition-all">
              ← Back
            </button>
            <button onClick={handleVerifyAndScan} disabled={verifying || otp.length !== 6}
              className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-black text-[11px]
                uppercase tracking-widest hover:bg-slate-900 transition-all disabled:opacity-60
                flex items-center justify-center gap-2">
              {verifying ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : "Verify & Run Scan"}
            </button>
          </div>
          <button onClick={handleSendOtp}
            className="w-full mt-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:text-red-600 transition-colors">
            Resend code
          </button>
        </motion.div>
      )}

      {step === "scanning" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="mt-2 bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4">
          <div className="flex items-center gap-3">
            <Loader2 size={16} className="animate-spin text-red-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-black text-slate-900 uppercase tracking-wide">Scanning {domain}</p>
              <p className="text-[10px] text-slate-400 font-medium mt-0.5">Checking exposed ports, CVEs and credential breaches…</p>
            </div>
          </div>
          <div className="mt-3 h-1 bg-slate-200 rounded-full overflow-hidden">
            <motion.div initial={{ width: "0%" }} animate={{ width: "85%" }}
              transition={{ duration: 3, ease: "easeOut" }} className="h-full bg-red-600 rounded-full" />
          </div>
        </motion.div>
      )}

      {(step === "error" || step === "rate_limited") && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className={`mt-2 rounded-2xl px-5 py-4 border flex items-start gap-3 ${
            step === "rate_limited" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"
          }`}>
          <AlertTriangle size={14} className={`flex-shrink-0 mt-0.5 ${step === "rate_limited" ? "text-amber-500" : "text-red-500"}`} />
          <div>
            <p className="text-xs font-black text-slate-900 mb-1">{step === "rate_limited" ? "Scan Limit Reached" : "Scan Failed"}</p>
            <p className="text-xs text-slate-600 font-medium">{errorMsg}</p>
            <div className="flex gap-3 mt-2">
              <button onClick={reset} className="text-[11px] font-black text-slate-500 uppercase tracking-wide hover:underline">Try again</button>
              {step === "rate_limited" && (
                <button onClick={onSignUp} className="text-[11px] font-black text-red-600 uppercase tracking-wide hover:underline">Sign up for unlimited scans →</button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {step === "done" && result && (
          <ScanResultCard key={result.domain} result={result} domain={result.domain ?? domain} onSignUp={onSignUp} />
        )}
      </AnimatePresence>

    </div>
  );
}

function PriceCard({ plan, annual, onSelect }: { plan: typeof plans[0]; annual: boolean; onSelect: () => void }) {
  const price   = annual ? plan.annualPrice   : plan.monthlyPrice;
  const savings = plan.monthlyPrice - plan.annualPrice;
  return (
    <motion.div layout
      className={`relative flex flex-col rounded-[2.5rem] border-2 p-8 md:p-10 transition-all duration-300
        ${plan.highlight ? "bg-[#0f172a] border-red-600 shadow-2xl shadow-red-900/20 md:scale-105 z-10 mt-6 md:mt-0" : "bg-white border-slate-100 hover:border-slate-200 hover:shadow-xl"}`}>
      {plan.highlight && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-red-600 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg whitespace-nowrap">Most Popular</span>
        </div>
      )}
      <h3 className={`text-xs font-black uppercase tracking-widest mb-3 ${plan.highlight ? "text-red-500" : "text-slate-400"}`}>{plan.tier}</h3>
      <div className="mb-2 flex items-baseline gap-1">
        <AnimatePresence mode="wait">
          <motion.span key={price} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2 }}
            className={`text-5xl font-black tracking-tighter ${plan.highlight ? "text-white" : "text-slate-900"}`}>${price}</motion.span>
        </AnimatePresence>
        <span className={`text-sm font-bold ${plan.highlight ? "text-white" : "text-slate-900"}`}>/mo</span>
      </div>
      <div className="mb-8 h-8 flex items-center">
        {annual ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
            <span className={`text-[10px] font-bold ${plan.highlight ? "text-slate-400" : "text-slate-400"}`}>Billed ${plan.annualPrice * 12}/yr</span>
            <span className="bg-emerald-100 text-emerald-700 text-[9px] font-black uppercase px-2 py-0.5 rounded-full">Save ${savings * 12}/yr</span>
          </motion.div>
        ) : (
          <span className={`text-[10px] font-bold ${plan.highlight ? "text-slate-500" : "text-slate-300"}`}>7-day free trial — then ${plan.monthlyPrice}/mo — cancel anytime</span>
        )}
      </div>
      <ul className="space-y-3 mb-10 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check size={15} className="flex-shrink-0 mt-0.5 text-red-500" />
            <span className={`text-sm font-bold ${plan.highlight ? "text-slate-300" : "text-slate-600"}`}>{f}</span>
          </li>
        ))}
      </ul>
      <button onClick={onSelect}
        className={`w-full py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all active:scale-[0.98] flex items-center justify-center gap-2 group ${
          plan.highlight ? "bg-red-600 text-white hover:bg-white hover:text-red-600 shadow-lg shadow-red-900/30" : "bg-slate-900 text-white hover:bg-red-600"}`}>
        Start 7-Day Free Trial
        <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
      </button>
    </motion.div>
  );
}

export default function Landing() {
  const navigate  = useNavigate();
  const [annual,      setAnnual]      = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  
  const [frameworkCount, setFrameworkCount] = useState(356);
  const [avgExposure, setAvgExposure] = useState(271);

  useEffect(() => {
    fetch(`${API_BASE}/api/stats/platform`)
      .then(r => r.json())
      .then(data => {
        if (data.avg_exposure_usd) {
          setAvgExposure(Math.round(data.avg_exposure_usd / 1000));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/frameworks/all`)
      .then(r => r.json())
      .then(data => {
        if (data.total) setFrameworkCount(data.total);
      })
      .catch(() => {});
  }, []);

  const stats = [
    { value: `${frameworkCount}+`, label: "Regulatory Frameworks Covered" },
    { value: "10,000+",            label: "Monte Carlo Simulations Per Audit" },
    { value: "34",                 label: "Technical Control Points" },
    { value: `$${avgExposure}K+`,  label: "Average Enterprise Exposure Found" },
  ];

  const handleGetStarted = async (tier: string = "Starter") => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/signup", { state: { initialMode: "signup", selectedTier: tier } }); return; }
    try {
      const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ tier: tier.toLowerCase(), billing: annual ? "annual" : "monthly" }),
      });
      const data = await res.json();
      if (data.checkout_url) { window.location.href = data.checkout_url; }
      else { navigate("/signup", { state: { initialMode: "signup", selectedTier: tier } }); }
    } catch { navigate("/signup", { state: { initialMode: "signup", selectedTier: tier } }); }
  };

  return (
    <div className="bg-[#0f172a] text-slate-900 font-['Inter'] selection:bg-red-100 overflow-x-hidden">
      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-[#0f172a]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div onClick={() => navigate("/")} className="flex items-center gap-3 cursor-pointer group">
            <div className="w-11 h-11 bg-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-900/30 group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-lg">V</span>
            </div>
            <span className="text-2xl font-black tracking-tighter text-white uppercase">VicShield</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors">Features</a>
            <a href="#pricing"  className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors">Pricing</a>
            <button onClick={() => navigate("/pricing")} className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors">Compare Plans</button>
            <button onClick={() => navigate("/login")}   className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-red-500 transition-colors">Sign In</button>
            <button onClick={() => window.open("https://calendly.com/waheed-vicshield/30min", "_blank")}
              className="border-2 border-white/30 text-white px-5 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-white hover:text-slate-900 transition-all">
              Book a Demo
            </button>
            <button onClick={() => handleGetStarted("Starter")}
              className="bg-red-600 text-white px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-900 shadow-md shadow-red-900/30 transition-all active:scale-95">
              Start 7-Day Free Trial
            </button>
          </div>
          <button onClick={() => navigate("/login")} className="md:hidden bg-red-600 text-white px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest">Sign In</button>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0f172a]">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: "32px 32px" }} />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-red-600 opacity-[0.06] rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-600 opacity-[0.04] rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-20 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 bg-red-600/10 border border-red-600/20 text-red-500 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-8">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              Enterprise Cyber Risk Intelligence Platform
            </div>

            
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-6 uppercase text-white">
              Know exactly{" "}
              <span className="relative">
                <span className="text-red-500">how much</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 10 Q150 2 298 10" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
                </svg>
              </span>
              {" "}your enterprise<br />is at risk.
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10 font-medium leading-relaxed">
              The elite platform that translates technical vulnerabilities into precise financial
              exposure figures giving boards, CISOs and insurers the intelligence they need.
            </p>

            {/* Domain Scanner */}
            <div className="mb-6">
              <AnimatePresence mode="wait">
                {!scannerOpen ? (
                  <motion.div key="scanner-prompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-3">
                    <button onClick={() => setScannerOpen(true)}
                      className="group bg-white/10 border-2 border-white/20 text-white px-8 py-3.5 rounded-2xl
                        font-black text-sm uppercase tracking-widest hover:border-red-400 hover:text-red-400
                        shadow-sm transition-all flex items-center gap-3">
                      <Search size={16} className="group-hover:scale-110 transition-transform" />
                      Scan Your Domain — Verify & See Your Risk
                    </button>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                      🔒 Corporate email verification required — your data stays protected
                    </p>
                  </motion.div>
                ) : (
                  <motion.div key="scanner-open" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full max-w-xl mx-auto">
                    <DomainScanner onSignUp={() => handleGetStarted("Starter")} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button onClick={() => handleGetStarted("Starter")}
                className="bg-red-600 text-white px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-white hover:text-red-600 shadow-xl shadow-red-900/30 transition-all hover:-translate-y-0.5 active:scale-95 flex items-center gap-3">
                Start 7-Day Free Trial
                <ChevronRight size={18} />
              </button>
              <button onClick={() => navigate("/login")}
                className="text-white px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest border-2 border-white/20 hover:border-white/40 transition-all">
                Sign In
              </button>
            </div>
            <p className="mt-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              7-day free trial · No credit card required · Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10 bg-[#1e293b]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <p className="text-3xl font-black text-white tracking-tighter">{s.value}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-[#0f172a] py-28">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-12 text-center">The Boardroom Reality</p>
          <div className="space-y-6">
            {[
              '"What is our maximum financial loss if a Data Breach occurs today?"',
              '"How does our security budget actually reduce our liability?"',
              '"Are we compliant with global standards, or just checking boxes?"',
            ].map((q, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className={`flex items-start gap-6 p-8 rounded-3xl border transition-all cursor-default group ${i === 0 ? "bg-white/5 border-red-600/40 text-white" : "border-white/5 text-slate-500 hover:text-slate-200 hover:bg-white/5 hover:border-white/10"}`}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${i === 0 ? "bg-red-600" : "bg-slate-600 group-hover:bg-red-600"} transition-colors`} />
                <p className="text-2xl font-bold leading-snug">{q}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-14 text-center text-sm text-red-500 font-black uppercase tracking-widest">Stop guessing. Start quantifying.</p>
        </div>
      </section>

            {/* PLATFORM PREVIEW */}
      <section className="py-20 bg-[#0f172a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: "32px 32px" }} />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-3">
              Live Intelligence Dashboard
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white uppercase tracking-tighter leading-tight mb-4">
              Visual Intelligence. Real-Time Posture.
            </h2>
            <p className="text-slate-400 font-medium leading-relaxed max-w-2xl mx-auto mb-2">
              See what VicShield produces for your sector. Select your industry below.
            </p>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Illustrative output — your actual figures are calculated after assessment
            </p>
          </div>

          {(() => {
            const [activeTab, setActiveTab] = React.useState(0);
            const sectors = [
              {
                label: "Technology / SaaS",
                score: 42,
                exposure: "$1.8M",
                tier: "Tier 2",
                threats: [
                  { label: "Data Breach",           pct: 82, amount: "$960,000"  },
                  { label: "Ransomware Deployment",  pct: 55, amount: "$480,000"  },
                  { label: "Phishing Attack",        pct: 38, amount: "$216,000"  },
                ],
                regs: ["SOC 2", "GDPR", "NDPA", "ISO 27001"],
              },
              {
                label: "Financial Services",
                score: 38,
                exposure: "$6.2M",
                tier: "Tier 1",
                threats: [
                  { label: "Data Breach",           pct: 91, amount: "$3,800,000" },
                  { label: "Ransomware Deployment",  pct: 67, amount: "$1,550,000" },
                  { label: "Insider Threat",         pct: 44, amount: "$620,000"   },
                ],
                regs: ["CBN", "DORA", "GLBA", "PCI-DSS"],
              },
              {
                label: "Healthcare",
                score: 31,
                exposure: "$9.1M",
                tier: "Tier 1",
                threats: [
                  { label: "Data Breach",           pct: 95, amount: "$5,600,000" },
                  { label: "Ransomware Deployment",  pct: 78, amount: "$2,300,000" },
                  { label: "Business Interruption",  pct: 52, amount: "$840,000"   },
                ],
                regs: ["HIPAA", "NHIA", "NHS DSPT", "GDPR"],
              },
            ];
            const s = sectors[activeTab];
            return (
              <div>
                {/* Sector tabs */}
                <div className="flex justify-center gap-3 mb-8 flex-wrap">
                  {sectors.map((sec, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTab(i)}
                      className={`px-5 py-2.5 rounded-xl text-[11px] font-black uppercase
                        tracking-widest transition-all border ${
                        activeTab === i
                          ? "bg-red-600 border-red-600 text-white"
                          : "bg-white/5 border-white/10 text-slate-400 hover:border-red-600 hover:text-white"
                      }`}
                    >
                      {sec.label}
                    </button>
                  ))}
                </div>

                {/* Dashboard mock */}
                <div className="bg-[#0a1628] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
                  {/* Dashboard header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-black text-sm">V</span>
                      </div>
                      <span className="text-white font-black text-sm uppercase tracking-tight">VicShield</span>
                      <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest
                        bg-emerald-400/10 px-2 py-1 rounded-lg border border-emerald-400/20">
                        Live Intelligence Active
                      </span>
                    </div>
                    <span className="text-slate-600 text-[10px] font-bold uppercase tracking-widest">VS-75451</span>
                  </div>

                  <div className="p-6">
                    {/* KPI cards */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {[
                        { label: "Security Posture Score",   value: `${s.score}`, suffix: "/100", color: "text-white" },
                        { label: "Annual Financial Exposure", value: s.exposure,   suffix: "",     color: "text-red-500" },
                        { label: "NIST Maturity Tier",       value: s.tier,        suffix: "",     color: "text-amber-400" },
                      ].map((m, i) => (
                        <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">{m.label}</p>
                          <div className="flex items-baseline gap-1">
                            <span className={`text-2xl font-black tracking-tighter ${m.color}`}>{m.value}</span>
                            {m.suffix && <span className="text-slate-500 text-sm font-bold">{m.suffix}</span>}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Threat breakdown */}
                      <div>
                        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-4">
                          Financial Exposure By Threat Vector
                        </p>
                        <div className="space-y-4">
                          {s.threats.map((t, i) => (
                            <div key={i}>
                              <div className="flex justify-between text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                                <span>{t.label}</span>
                                <span className="text-red-400">{t.amount}</span>
                              </div>
                              <div className="w-full bg-white/5 rounded-full h-1.5">
                                <div className="bg-red-600 h-1.5 rounded-full transition-all duration-500"
                                  style={{ width: `${t.pct}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Compliance radar */}
                      <div>
                        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-4">
                          Applicable Regulatory Frameworks
                        </p>
                        <div className="space-y-2">
                          {s.regs.map((r, i) => (
                            <div key={i} className="flex items-center justify-between
                              bg-white/5 border border-white/5 rounded-xl px-4 py-2.5">
                              <span className="text-xs font-black text-white">{r}</span>
                              <span className={`text-[9px] font-black uppercase tracking-widest
                                ${i === 1 ? "text-red-400" : i === 2 ? "text-amber-400" : "text-emerald-400"}`}>
                                {i === 1 ? "GAPS FOUND" : i === 2 ? "REVIEW" : "MONITORED"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

{/* FEATURES */}
      <section id="features" className="py-28 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-4">Platform Capabilities</p>

          <h2 className="text-4xl font-black tracking-tighter uppercase text-red-500">Every tool your security team needs.</h2>

        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="group p-8 rounded-3xl border-2 border-slate-100 hover:border-red-100 hover:shadow-xl hover:shadow-red-50 transition-all duration-300 bg-white text-left">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-red-600 group-hover:text-white transition-all shadow-sm">
                <f.icon size={24} />
              </div>
              <h3 className="text-lg font-black mb-3 tracking-tight uppercase">{f.title}</h3>
              <p className="text-slate-500 leading-relaxed text-sm font-medium">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

                  {/* ── THREAT SCENARIO QUANTIFICATION ────────────────────────────── */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">
              Financial Risk Quantification
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 uppercase tracking-tighter leading-tight mb-4">
              Know Your Exact Cost For<br />Every Cyber Threat Scenario
            </h2>
            <p className="text-slate-500 font-medium leading-relaxed max-w-2xl mx-auto">
              VicShield quantifies your financial exposure across every major cyber
              threat vector using actuarial modelling calibrated against IBM, Verizon
              and Ponemon industry breach data. Your actual figures depend on your
              specific sector, revenue and controls.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: "💥",
                threat: "Data Breach",
                avgCost: "$4.44M",
                avgLabel: "Global industry average cost",
                hook: "Per-record cost × your data volume + notification, legal, regulatory fines and reputational damage.",
                stats: [
                  { label: "Avg. detection time",  value: "241 days" },
                  { label: "Avg. containment",      value: "64 days"  },
                  { label: "Healthcare sector",     value: "$7.42M"   },
                  { label: "Financial sector",      value: "$6.08M"   },
                ],
                source: "IBM Cost of Data Breach 2025",
                color: "border-red-100 bg-red-50/30",
                dot: "bg-red-500",
              },
              {
                icon: "🔒",
                threat: "Ransomware Attack",
                avgCost: "$2.73M",
                avgLabel: "Average recovery cost",
                hook: "Downtime revenue loss, recovery costs, ransom payment range and business interruption modelled against your profile.",
                stats: [
                  { label: "Avg. downtime",        value: "21 days"   },
                  { label: "Median ransom",         value: "$115,000"   },
                  { label: "% of all breaches",    value: "44%"       },
                  { label: "Avg. recovery time",   value: "3–6 weeks" },
                ],
                source: "Verizon DBIR 2025",
                color: "border-amber-100 bg-amber-50/30",
                dot: "bg-amber-500",
              },
              {
                icon: "👤",
                threat: "Insider Threat",
                avgCost: "$17.4M",
                avgLabel: "Average annual cost per incident",
                hook: "IP theft, data exfiltration, fraud and detection costs modelled against your employee count and data classification.",
                stats: [
                  { label: "Avg. detection time",  value: "81 days"  },
                  { label: "% of all breaches",    value: "30%"       },
                  { label: "Malicious insider",    value: "$715K avg" },
                  { label: "Negligent insider",    value: "$677K avg" },
                ],
                source: "Ponemon Insider Threat Report 2025",
                color: "border-purple-100 bg-purple-50/30",
                dot: "bg-purple-500",
              },
              {
                icon: "⏱️",
                threat: "Business Interruption",
                avgCost: "$300K/hr",
                avgLabel: "Average downtime cost",
                hook: "Revenue loss per hour, SLA penalty exposure, customer churn cost and recovery investment based on your revenue profile.",
                stats: [
                  { label: "Avg. incident duration", value: "1–21 days" },
                  { label: "SLA breach penalties",   value: "Up to 30%" },
                  { label: "Customer churn risk",    value: "15-40%  "   },
                  { label: "Enterprise avg.",         value: "$300K/hr"  },
                ],
                source: "ITIC 2025 & IBM 2025",
                color: "border-blue-100 bg-blue-50/30",
                dot: "bg-blue-500",
              },
              {
                icon: "🎣",
                threat: "Phishing & BEC",
                avgCost: "$123,000",
                avgLabel: "Average cost per incident",
                hook: "Business Email Compromise, credential theft and social engineering the most common entry point for all major cyber attacks globally.",
                stats: [
                  { label: "% of all breaches",      value: "36%"      },
                  { label: "Avg. BEC loss",           value: "$3.04B" },
                  { label: "Avg. detection time",    value: "241 days"  },
                  { label: "Training risk reduction", value: "70%"      },
                ],
                source: "IBM 2025 & FBI IC3 2025",
                color: "border-orange-100 bg-orange-50/30",
                dot: "bg-orange-500",
              },
              {
                icon: "💳",
                threat: "Payment Fraud",
                avgCost: "$125,000",
                avgLabel: "Average loss per incident",
                hook: "Authorised push payment fraud, account takeover and transaction manipulation, a direct financial crime targeting your payment infrastructure.",
                stats: [
                  { label: "Global fraud losses",    value: "$33.4B"    },
                  { label: "Orgs targeted",          value: "79%"       },
                  { label: "APF fraud increase",     value: "42%"       },
                  { label: "BEC per incident",       value: "$123K avg" },
                ],
                source: "Nilson Report 2025 & AFP 2025",
                color: "border-green-100 bg-green-50/30",
                dot: "bg-green-500",
              },
            ].map((card, i) => (
              <div key={i} className={`rounded-3xl border-2 ${card.color} p-6 hover:shadow-lg transition-all`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{card.icon}</span>
                    <div>
                      <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{card.threat}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${card.dot}`} />
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Industry Average</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black text-slate-900">{card.avgCost}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest max-w-[100px] text-right">{card.avgLabel}</p>
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-500 leading-relaxed mb-4">{card.hook}</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {card.stats.map((s, j) => (
                    <div key={j} className="bg-white/70 rounded-xl p-2.5 border border-white">
                      <p className="text-xs font-black text-slate-900">{s.value}</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{s.label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">
                  Source: {card.source}
                </p>
              </div>
            ))}
          </div>

          {/* CTA — drives to assessment for real numbers */}
          <div className="bg-slate-900 rounded-3xl p-8 text-center">
            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-3">
              Your Numbers Are Different
            </p>
            <h3 className="text-2xl font-black text-white uppercase tracking-tighter mb-3">
              Industry Averages Are Not Your Numbers
            </h3>
            <p className="text-slate-300 font-medium leading-relaxed max-w-2xl mx-auto mb-6">
              Your actual exposure depends on your sector, revenue, employee count,
              data volume and existing security controls. VicShield runs 10,000 Monte
              Carlo simulations calibrated specifically to your organisation producing
              P90 and P99 unique confidence intervals.
            </p>
            <button
              onClick={() => navigate("/login", { state: { initialMode: "signup" } })}
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-600
                text-white rounded-2xl font-black text-[11px] uppercase tracking-widest
                hover:bg-white hover:text-slate-900 transition-all shadow-xl shadow-red-900/30"
            >
              Find Out Your Exact Exposure — Free Trial
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-4">
              All plans · 7-day free trial · No credit card required · Cancel anytime
            </p>
          </div>
        </div>
      </section>

{/* PRICING */}
      <section id="pricing" className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-4">Transparent Pricing</p>
            <h2 className="text-4xl font-extrabold tracking-tighter uppercase mb-4">Elite Protection Plans</h2>
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-5 py-2 mb-8">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <p className="text-xs font-black text-emerald-700 uppercase tracking-wide">All plans include a 7-day free trial — card saved, not charged until day 8</p>
            </div>
            <div className="inline-flex items-center gap-4 bg-white border-2 border-slate-100 rounded-2xl p-1.5 shadow-sm">
              <button onClick={() => setAnnual(false)}
                className={`px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 ${!annual ? "bg-slate-900 text-white shadow-md" : "text-slate-400 hover:text-slate-700"}`}>
                Monthly
              </button>
              <button onClick={() => setAnnual(true)}
                className={`px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 flex items-center gap-2 ${annual ? "bg-slate-900 text-white shadow-md" : "text-slate-400 hover:text-slate-700"}`}>
                Annually
                <AnimatePresence>
                  {!annual && (
                    <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                      className="bg-emerald-100 text-emerald-700 text-[9px] font-black uppercase px-2 py-0.5 rounded-full">Save 20%</motion.span>
                  )}
                </AnimatePresence>
              </button>
            </div>
            {annual && (
              <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="mt-4 text-xs font-bold text-emerald-600 uppercase tracking-widest">
                🎉 You're saving up to $2,400/year on annual billing
              </motion.p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center max-w-5xl mx-auto px-4 pt-6 md:pt-0">
            {plans.map((plan) => (
              <PriceCard key={plan.tier} plan={plan} annual={annual} onSelect={() => handleGetStarted(plan.tier)} />
            ))}
          </div>
          <p className="text-center text-[10px] font-bold text-slate-300 uppercase tracking-widest mt-10">
            All plans · 7-day free trial · No credit card required · Cancel anytime
          </p>
          <div className="text-center mt-6">
            <button onClick={() => navigate("/pricing")} className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-red-600 hover:text-slate-900 transition-colors">
              See full feature comparison <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </section>

            {/* ── GLOBAL REGULATORY URGENCY ─────────────────────────────────── */}
      <section className="py-16 bg-[#0f172a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`, backgroundSize: "32px 32px" }} />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-3">Global Regulatory Alert</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white uppercase tracking-tighter leading-tight mb-4">
              Regulatory Fines Are<br /><span className="text-red-500">Increasing Globally</span>
            </h2>
            <p className="text-slate-300 font-medium leading-relaxed max-w-2xl mx-auto">
              Governments worldwide are enforcing data protection and cybersecurity regulations
              with unprecedented financial penalties. VicShield maps your exact exposure
              across all applicable frameworks automatically.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {[
              { flag: "🇪🇺", region: "European Union", law: "GDPR / DORA / NIS2",    fine: "€20M or 4% of global turnover",    sector: "All sectors" },
              { flag: "🇺🇸", region: "United States",  law: "SOX / NYDFS / SEC",      fine: "Up to $5M per violation",          sector: "All sectors" },
              { flag: "🇬🇧", region: "United Kingdom", law: "UK GDPR / FCA",           fine: "£17.5M or 4% of global turnover",  sector: "All sectors" },
              { flag: "🇳🇬", region: "Nigeria",        law: "NDPA / CBN / NITDA",      fine: "₦10M or 2% of annual revenue",     sector: "All sectors" },
              { flag: "🇦🇺", region: "Australia",      law: "Privacy Act / APRA",      fine: "AUD $50M or 30% of turnover",      sector: "All sectors" },
              { flag: "🇮🇳", region: "India",          law: "DPDP Act / RBI / SEBI",   fine: "₹250 crore (~$30M USD)",           sector: "All sectors" },
              { flag: "🇸🇬", region: "Singapore",      law: "PDPA / MAS TRM",          fine: "SGD $1M per breach",               sector: "Finance & All" },
              { flag: "🇨🇦", region: "Canada",         law: "PIPEDA / Quebec Law 25",  fine: "CAD $25M or 4% of turnover",       sector: "All sectors" },
              { flag: "🇿🇦", region: "South Africa",   law: "POPIA / Cybercrime Act",  fine: "ZAR 10M + criminal prosecution",   sector: "All sectors" },
              { flag: "🇦🇪", region: "UAE",            law: "PDPL / NESA / CBUAE",     fine: "AED 5M per violation",             sector: "All sectors" },
              { flag: "🇸🇦", region: "Saudi Arabia",   law: "PDPL / NCA ECC / SAMA",   fine: "SAR 5M per violation",             sector: "All sectors" },
              { flag: "🇯🇵", region: "Japan",          law: "APPI / FSA",              fine: "¥100M per violation",              sector: "All sectors" },
              { flag: "🇨🇳", region: "China",          law: "PIPL / CSL / DSL",        fine: "RMB 50M or 5% of revenue",         sector: "All sectors" },
              { flag: "🇧🇷", region: "Brazil",         law: "LGPD / BACEN",            fine: "R$50M or 2% of revenue",           sector: "All sectors" },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-black text-white bg-white/20 px-2 py-0.5 rounded-lg">{item.flag}</span>
                  <div>
                    <p className="text-xs font-black text-white">{item.region}</p>
                  <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">{item.law}</p>
                  </div>
                </div>
                <p className="text-sm font-black text-red-400 mb-1">{item.fine}</p>
                <p className="text-[10px] font-medium text-slate-400">{item.sector}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="text-slate-400 font-medium text-sm mb-6">
              VicShield automatically detects which regulations apply to your business
              and checks compliance across all of them simultaneously.
            </p>
            <button onClick={() => navigate("/frameworks")}
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 text-white rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-white hover:text-slate-900 transition-all">
              See All 356+ Frameworks We Cover →
            </button>
          </div>
        </div>
      </section>

      {/* ── INSURANCE INTELLIGENCE ───────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">Insurance Intelligence</p>
              <h2 className="text-3xl font-extrabold text-slate-900 uppercase tracking-tighter leading-tight mb-4">
                Reduce Your Cyber<br />Insurance Premium
              </h2>
              <p className="text-slate-500 font-medium leading-relaxed mb-6">
                Companies with documented, actuarial-grade cyber risk assessments typically qualify
                for significantly reduced cyber insurance premiums. VicShield generates
                underwriter-ready reports formatted for direct submission to cyber insurance
                underwriters globally including regulated insurers across North America, Europe,
                Africa, Asia-Pacific and the Middle East.
              </p>
              <div className="space-y-3">
                {["Actuarial output formatted for direct submission to underwriters",
                  "Compatible with insurer documentation requirements across 34 countries",
                  "Meets local insurer documentation requirements globally",
                  "Premium reduction projections included in every report",
                  "Insurance Intelligence available on Professional and Enterprise plans",
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-red-600 font-black text-sm flex-shrink-0 mt-0.5">✓</span>
                    <p className="text-sm font-medium text-slate-600">{p}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-6">Sample Actuarial Insurance Output</p>
              {[
                { label: "Est. Annual Premium", value: "$48,000/yr",  color: "text-slate-900" },
                { label: "Potential Saving",    value: "$12,000/yr",  color: "text-emerald-600" },
                { label: "Insurability Rating", value: "Standard",    color: "text-amber-600" },
                { label: "After Remediation",   value: "Preferred",   color: "text-emerald-600" },
                { label: "Coverage Capacity",   value: "Unlocked",    color: "text-emerald-600" },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{r.label}</p>
                  <p className={`text-sm font-black ${r.color}`}>{r.value}</p>
                </div>
              ))}
              <p className="text-[9px] font-medium text-slate-400 mt-4">Estimate only � Actual premium varies by insurer, sector and controls</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── THREAT INTELLIGENCE ──────────────────────────────────────────── */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">External Threat Intelligence</p>
            <h2 className="text-3xl font-extrabold text-slate-900 uppercase tracking-tighter leading-tight mb-4">
              See Your Attack Surface<br />Before Attackers Do
            </h2>
            <p className="text-slate-500 font-medium leading-relaxed max-w-2xl mx-auto">
              VicShield performs live passive reconnaissance on your domain
              detecting exposed ports, known CVE vulnerabilities and credential
              breaches before attackers find them.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: "🔍", title: "Live Port Scanning",          stat: "Real-Time", statLabel: "Reconnaissance",    desc: "Detects exposed services, open ports and misconfigured infrastructure on your domain using passive intelligence, no intrusive scanning required." },
              { icon: "⚠️", title: "CVE Vulnerability Detection", stat: "50,000+",   statLabel: "CVEs Monitored",   desc: "Identifies known CVE vulnerabilities affecting your exposed services and infrastructure mapped against your specific software versions." },
              { icon: "🔐", title: "Credential Breach Monitoring",stat: "Dark Web",  statLabel: "Monitoring Active", desc: "Continuously monitors dark web sources for your corporate domain identifying compromised employee credentials before attackers use them." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-3xl p-6 border border-slate-100 hover:border-red-100 hover:shadow-lg transition-all">
                <span className="text-3xl mb-4 block">{item.icon}</span>
                <p className="text-sm font-black text-slate-900 uppercase tracking-tight mb-2">{item.title}</p>
                <p className="text-xs font-medium text-slate-500 leading-relaxed mb-4">{item.desc}</p>
                <div className="pt-4 border-t border-slate-50">
                  <p className="text-lg font-black text-red-600">{item.stat}</p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{item.statLabel}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VENDOR RISK ──────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="bg-slate-900 rounded-3xl p-8 order-2 md:order-1">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-5">Vendor Risk Matrix</p>
              {[
                { name: "AWS / Cloud Infrastructure", score: 87, status: "VERIFIED",      color: "text-emerald-400" },
                { name: "Payment Gateway API",         score: 54, status: "ACTION REQ.",   color: "text-red-400" },
                { name: "CRM / Marketing Platform",    score: 62, status: "MONITORING",    color: "text-amber-400" },
                { name: "Data Archival Provider",      score: 91, status: "SECURE",        color: "text-emerald-400" },
                { name: "HR Management System",        score: 43, status: "CRITICAL RISK", color: "text-red-400" },
              ].map((v, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0 gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-black text-white truncate">{v.name}</p>
                    <div className="w-full bg-white/10 rounded-full h-1 mt-1.5">
                      <div className={`h-1 rounded-full ${v.score >= 80 ? "bg-emerald-400" : v.score >= 60 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${v.score}%` }} />
                    </div>
                  </div>
                  <span className={`text-[9px] font-black uppercase tracking-widest flex-shrink-0 ${v.color}`}>{v.status}</span>
                </div>
              ))}
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">Vendor Risk Intelligence</p>
              <h2 className="text-3xl font-extrabold text-slate-900 uppercase tracking-tighter leading-tight mb-4">
                Your Vendors Are Your<br />Biggest Hidden Risk
              </h2>
              <p className="text-slate-500 font-medium leading-relaxed mb-6">
                Supply chain attacks accounted for 15% of all breaches in 2024 (Verizon DBIR).
                Every vendor with API access to your systems is a potential entry point.
                VicShield scores your entire vendor ecosystem for inherited risk.
              </p>
              <div className="space-y-3">
                {["Score every vendor 0–100 on security posture",
                  "Flag critical and high-risk vendors automatically",
                  "Generate vendor risk reports for board and insurers",
                  "Meets ISO 27001, NDPA and DORA third-party requirements",
                  "Track vendor compliance across all your entities",
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-red-600 font-black text-sm flex-shrink-0 mt-0.5">✓</span>
                    <p className="text-sm font-medium text-slate-600">{p}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

{/* WHY VICSHIELD */}
      <section className="py-28 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-4">Platform Intelligence</p>
            <h2 className="text-4xl font-black tracking-tighter uppercase text-white">Why Security Leaders Choose VicShield.</h2>
            <p className="text-slate-400 font-medium mt-4 max-w-2xl mx-auto">
              Built on the same actuarial standards used by enterprise risk teams accessible to every organisation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: "$", title: "Board-Ready Financial Figures", desc: "Translates technical vulnerabilities into exact dollar exposure the number your board actually needs." },
              { icon: "⚡", title: "15 Minutes to Full Report", desc: "From assessment submission to board-ready PDF report, no waiting, no manual analysis required." },
              { icon: "📊", title: "IBM & Verizon DBIR Calibrated", desc: "Every figure grounded in IBM Cost of Data Breach 2026 and Verizon DBIR 2026  not estimates." },
              { icon: "🌍", title: "34 Countries, 356+ Frameworks", desc: "Regulatory compliance mapped automatically across every major jurisdiction in one platform." },
              { icon: "🛡️", title: "Insurance Premium Reduction", desc: "Generates underwriter-ready documentation to qualify for reduced cyber insurance premiums." },
              { icon: "🔗", title: "Enterprise API Integration", desc: "Connect VicShield data directly to PowerBI, Splunk, Tableau and custom dashboards via REST API." },
            ].map((item, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:border-red-500/30 hover:bg-white/10 transition-all">
                <div className="text-3xl mb-4 text-emerald-400">{item.icon}</div>
                <h3 className="text-sm font-black text-red-500 uppercase tracking-tight mb-3">{item.title}</h3>
                <p className="text-slate-400 font-medium leading-relaxed text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

            {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">
              Frequently Asked Questions
            </p>
            <h2 className="text-3xl font-extrabold text-slate-900 uppercase tracking-tighter leading-tight">
              Everything You Need To Know
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "How long does an assessment take?",
                a: "Most assessments take 15–20 minutes to complete. Results, reports and your full compliance radar are available immediately after submission, no waiting period.",
              },
              {
                q: "Is my data secure with VicShield?",
                a: "Yes. All data is encrypted at rest and in transit using AES-256 encryption. Your assessment data is never sold, shared or used for any purpose other than generating your intelligence reports. VicShield is built on enterprise-grade infrastructure aligned to SOC2 and ISO/IEC 27001 security standards.",
              },
              {
                q: "What countries and regions does VicShield cover?",
                a: "VicShield covers 34 countries and jurisdictions: United Kingdom, United States, European Union, Canada, Australia, UAE, Saudi Arabia, India, Singapore, South Africa, Nigeria and more with 356+ regulatory frameworks automatically mapped to your business. More countries are added regularly.",
              },
              {
                q: "What currencies are supported?",
                a: "VicShield supports 50+ currencies including USD, GBP, EUR, NGN, AED, AUD, CAD, ZAR, INR, SAR and SGD. All financial exposure outputs, insurance estimates and cost projections are presented in your chosen currency.",
              },
              {
                q: "How is VicShield different from a manual security audit?",
                a: "A traditional manual audit takes 6–8 weeks, costs $30,000–$50,000 and gives you a one-time snapshot. VicShield delivers results in 15 minutes from $199/month with continuous monitoring, real-time regulatory updates and automatic re-assessment as your business changes.",
              },
              {
                q: "What happens after the 7-day free trial?",
                a: "Your 7-day free trial gives you full access to VicShield's Starter plan features including your security posture score, financial exposure figures, compliance radar across 356+ frameworks, domain intelligence scan, and your downloadable Standard 13-page Audit Report. No credit card required. After 7 days choose to subscribe or cancel no hidden fees, no lock-in..",
              },
              {
                q: "Can I assess multiple business entities?",
                a: "Yes. The Professional plan covers 2 entities and the Enterprise plan covers up to 5 entities. Each entity gets its own full assessment, report, compliance radar, domain scan and financial exposure calculation. Contact us for custom multi-entity arrangements beyond 5.",
              },
              {
                q: "Which specific regulations does VicShield cover?",
                a: "VicShield covers 356+ frameworks including GDPR, NDPA, CBN, HIPAA, DORA, NIS2, FCA, MAS TRM, POPIA, APRA CPS 234, PCI-DSS, ISO 27001, NIST CSF and many more. Applicable frameworks are automatically detected based on your sector, geography and customer base. See the full list at vicshield.com/frameworks.",
              },
            ].map((item, i) => {
              const [open, setOpen] = React.useState(false);
              return (
                <div key={i} className="border-2 border-slate-100 rounded-2xl overflow-hidden hover:border-red-100 transition-all">
                  <button
                    onClick={() => setOpen(!open)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <p className="text-sm font-black text-slate-900 pr-4">{item.q}</p>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 border-slate-200
                      flex items-center justify-center transition-all ${open ? "bg-red-600 border-red-600 rotate-45" : ""}`}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                        stroke={open ? "white" : "#94a3b8"} strokeWidth="3">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                    </div>
                  </button>
                  {open && (
                    <div className="px-5 pb-5 border-t border-slate-50">
                      <p className="text-sm font-medium text-slate-500 leading-relaxed pt-4">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <p className="text-sm font-medium text-slate-400 mb-3">
              Have a question not answered here?
            </p>
            <a href="mailto:hello@vicshield.com"
              className="text-[11px] font-black uppercase tracking-widest text-red-600
                hover:text-slate-900 transition-colors">
              Contact Us → support@vicshield.com
            </a>
          </div>
        </div>
      </section>

{/* FINAL CTA */}
      <section className="py-28 bg-[#0f172a] relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-600 opacity-[0.06] rounded-full blur-[120px] pointer-events-none" />
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="relative z-10 text-center max-w-3xl mx-auto px-6">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-500 mb-6">Get Started Today</p>
          <h2 className="text-5xl font-extrabold text-white tracking-tighter uppercase leading-tight mb-8">
            Ready to lead with{" "}<span className="text-red-500">Intelligence?</span>
          </h2>
          <p className="text-slate-400 font-medium mb-10 leading-relaxed">
            Join enterprise security teams who have transformed their cyber risk posture from guesswork into precise, board-ready intelligence.
          </p>
          <button onClick={() => handleGetStarted("Starter")}
            className="bg-red-600 text-white px-12 py-5 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-white hover:text-red-600 shadow-2xl shadow-red-900/30 transition-all hover:scale-105 active:scale-95 inline-flex items-center gap-3">
            Start Your Free 7-Day Trial
            <ArrowRight size={18} />
          </button>
          <p className="mt-4 text-[10px] font-bold text-slate-600 uppercase tracking-widest">7-day free trial · Card not charged for 7 days · All plans included</p>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0f172a] border-t border-white/5 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-sm">V</span>
            </div>
            <div>
              <span className="text-slate-500 text-[10px] font-black uppercase tracking-widest">© 2026 VicShield. All rights reserved.</span>
              <p className="text-slate-600 text-[10px] font-bold mt-1">
                Official domain: <span className="text-red-500">vicshield.com</span> only — beware of imitations
              </p>
            </div>
          </div>
          <div className="flex gap-8">
            <button onClick={() => navigate("/privacy")} className="text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-red-500 transition-colors">Privacy Policy</button>
            <button onClick={() => navigate("/terms")}   className="text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-red-500 transition-colors">Terms of Service</button>
            <button onClick={() => navigate("/contact")} className="text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-red-500 transition-colors">Contact</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
